import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Image from "components/Image";
import Button from "components/Button";
import EditProductDialog from "pages/edit-products/EditProductDialog";
import AddCircleIcon from "@material-ui/icons/AddCircle";
import { getStyles, getBrands, getPromos } from "selectors/tags";
import Tooltip from "@material-ui/core/Tooltip";
import Select from "components/Select";
import { convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import _ from "lodash";
import SearchBar from "components/SearchBar";
import ShirtlyProductSync from "pages/edit-products/ShirtlyProductSync";
import {
  editProducts,
  editProductSize,
  editColorsInformation,
  editDesignerSettings,
  editAdditionalTechniquesArtSettings,
  editPrintProvidersInformation,
  deleteProduct,
  editDTGPlusInformation,
  editArtworkSettings,
  editProductAdditionalImages,
  clearAdditionalImages,
  getAllProducts,
  syncProductsFromShirtly,
  saveProductsInBot,
  editPrintAreas,
  editSKUInformation,
} from "actions/products";
import {
  Container,
  AdminItemGroup,
  SearchWrapper,
  Item,
  ProductLink,
  ProductImage,
  Description,
  Text,
  Amount,
  DialogTitle,
} from "styles/pages/browse-catalog/details.js";
import { duplicateProductInformationPayload } from "pages/edit-products/data";

const EditProducts = () => {
  const products = useSelector((state) => state?.products?.data);
  const categories = useSelector((state) => state?.categories?.data);
  const tags = useSelector((state) => state?.tags?.data);
  const styleTags = getStyles(tags) || [];
  const brandsTags = getBrands(tags) || [];
  const promosTags = getPromos(tags) || [];
  const [selectedProduct, setProduct] = useState(undefined);
  const [search, setSearch] = useState(undefined);
  const dispatch = useDispatch();
  const [shirtlySyncProducts, setShirtlySyncProducts] = useState(undefined);
  const [printProvider, setPrintProvider] = useState(undefined);
  const [decoration, setDecoration] = useState(undefined);

  const data = printProvider
    ? products?.filter((p) =>
        p?.PrintProviders?.find((pp) => pp?.ProviderName === printProvider)
      )
    : decoration
    ? products?.filter(
        (p) =>
          p?.Product?.PrintType?.toLowerCase() === decoration?.toLowerCase() ||
          p?.AdditionalTechniquesArtSettings?.filter(
            (q) => q?.name?.toLowerCase() === decoration?.toLowerCase()
          )?.length > 0
      )
    : search
    ? products?.filter((p) =>
        _.includes(p?.Product?.DisplayName?.toLowerCase(), search.toLowerCase())
      )
    : products;

  return (
    <div style={{ padding: 15 }}>
      <Container>
        <DialogTitle>
          Edit Product ({products?.length})
          <Button
            title="Sync Products with Shirtly"
            onClick={async () => {
              const data = await dispatch(syncProductsFromShirtly());
              if (data?.error) {
                return;
              }
              let newProducts = [];
              for (let i = 0; i < data?.length; i++) {
                let found = false;
                for (let j = 0; j < products?.length; j++) {
                  if (
                    data[i]?.Product?.DisplayName?.trim() ===
                    products[j]?.Product?.DisplayName?.trim()
                  ) {
                    found = true;
                    break;
                  }
                }
                if (!found) {
                  newProducts.push(data[i]);
                }
              }
              newProducts = newProducts.map((p) => _.omit(p, "_id"));
              setShirtlySyncProducts(newProducts);
            }}
            containerStyle={{
              float: "right",
              cursor: "pointer",
              color: "#fff",
              backgroundColor: "#000",
              fontSize: 12,
              margin: "3px 0 10px 10px",
              padding: "5px 10px",
            }}
          />
          <Button
            style={{ display: "none" }}
            title="Update DTG Price"
            containerStyle={{
              float: "right",
              cursor: "pointer",
              color: "#fff",
              backgroundColor: "#000",
              fontSize: 12,
              margin: "3px 0 10px 10px",
              padding: "5px 10px",
            }}
            onClick={async () => {
              let newProducts = [];

              for (let i = 0; i < products?.length; i++) {
                const product = products[i];
                const printProviders = product?.PrintProviders;

                let isValidPrintprovider = true;

                for (let j = 0; j < printProviders.length; j++) {
                  const providerName = printProviders[j]?.ProviderName;

                  if (providerName !== "CA" && providerName !== "US") {
                    isValidPrintprovider = false;
                    break;
                  }
                }

                if (isValidPrintprovider) {
                  newProducts.push(product);
                }
              }

              for (let i = 0; i < newProducts.length; i++) {
                let sizes = [];

                for (let j = 0; j < newProducts[i]?.Sizes.length; j++) {
                  const size = newProducts[i]?.Sizes[j];

                  if (size?.DTGPrice) {
                    size.DTGPrice = 6;
                  }

                  if (size?.EMBPrice) {
                    size.EMBPrice = 10;
                  }

                  if (size?.DURASOFTPrice) {
                    size.DURASOFTPrice = 6.5;
                  }

                  sizes.push(size);
                }

                await dispatch(
                  editProductSize(
                    {
                      ID: newProducts[i]?.Product?.ID,
                      sizeData: sizes,
                    },
                    false
                  )
                );
              }
            }}
          />
          <Tooltip title="Add New Product">
            <AddCircleIcon
              style={{
                float: "right",
                cursor: "pointer",
                color: "#000",
                marginTop: "5px",
                fontSize: 30,
              }}
              onClick={() => setProduct({})}
            />
          </Tooltip>
        </DialogTitle>

        <SearchWrapper style={{ display: "grid", gap: "20px" }}>
          <SearchBar
            fetchAllOrders={() => setSearch(undefined)}
            searchById={(value) => setSearch(value || undefined)}
          />

          <Select
            label="Select Print Provider"
            value={printProvider}
            options={["US", "CA", "EU", "AU", "NY"]}
            onChange={(v) => {
              setDecoration(null);
              setPrintProvider(v);
            }}
          />

          <Select
            label="Select Decoration"
            value={decoration}
            options={[
              "DTG",
              "Plus",
              "DTGPlus",
              "EMB",
              "DURASOFT",
              "AOP",
              "SUB",
              "SCREENPRINT",
            ]}
            onChange={(v) => {
              setPrintProvider(null);
              setDecoration(v);
            }}
          />
        </SearchWrapper>

        <AdminItemGroup>
          {data?.map((item, index) => {
            const product = item.Product;

            return (
              <Item
                key={`productsCatalog${index}`}
                onClick={() => setProduct(item)}
              >
                <ProductLink>
                  <Tooltip title="Duplicate" arrow={true}>
                    <FileCopyIcon
                      style={{
                        position: "absolute",
                        right: 10,
                        zIndex: 9,
                        color: "#000",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        e.preventDefault();

                        const payload =
                          duplicateProductInformationPayload(item);
                        const request = await dispatch(
                          editProducts(payload, false)
                        );
                        const productId = request?.Product?.ID;

                        await dispatch(
                          editProductSize(
                            {
                              ID: productId,
                              sizeData: item?.Sizes,
                            },
                            false
                          )
                        );

                        await dispatch(
                          editColorsInformation(
                            {
                              ID: productId,
                              colorsData: item?.Colors,
                            },
                            false
                          )
                        );

                        await dispatch(
                          editDesignerSettings(
                            {
                              ID: productId,
                              designerSettingsData: item?.DesignerSettings,
                              backDesignerSettingsData:
                                item?.BackDesignerSettings,
                            },
                            false
                          )
                        );

                        await dispatch(
                          editArtworkSettings(
                            {
                              ID: productId,
                              artworkSettingsData: {
                                greyOut: item?.ArtworkSettings?.greyOut,
                                allColors: item?.ArtworkSettings?.allColors,
                              },
                            },
                            false
                          )
                        );

                        await dispatch(
                          editPrintProvidersInformation(
                            {
                              ID: productId,
                              printProvidersData: item?.PrintProviders,
                            },
                            false
                          )
                        );

                        await dispatch(
                          editDTGPlusInformation(
                            {
                              ID: productId,
                              dtgPlusData: item?.DTGPlus,
                              remainingDecorativeTechniques:
                                item?.RemainingDecorativeTechniques,
                            },
                            false
                          )
                        );

                        await dispatch(
                          editProductAdditionalImages(
                            {
                              ID: productId,
                              additionalImages: item?.AdditionalImages,
                            },
                            false
                          )
                        );

                        await dispatch(getAllProducts());
                      }}
                    />
                  </Tooltip>
                  <ProductImage>
                    <Image
                      src={product?.MarketingImagePath}
                      alt={product?.DisplayName}
                    />
                  </ProductImage>
                  <Description>
                    <Text>{product?.DisplayName}</Text>
                    <Amount>
                      from ${item?.Sizes ? item?.Sizes[0]?.ColoredPrice : 0} USD
                    </Amount>
                  </Description>
                </ProductLink>
              </Item>
            );
          })}
        </AdminItemGroup>
        {!!selectedProduct && (
          <EditProductDialog
            open={!!selectedProduct}
            setModal={(v) => setProduct(undefined)}
            product={selectedProduct}
            categories={categories}
            styleTags={styleTags}
            brandsTags={brandsTags}
            promosTags={promosTags}
            editSKUInformation={async (payload) => {
              const request = await dispatch(editSKUInformation(payload));
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            updatePrintArea={async (areas, additionalTechniquesArtSettings) => {
              const request = await dispatch(
                editPrintAreas({
                  ID: selectedProduct?.Product?.ID,
                  printAreas: areas,
                })
              );

              if (request.error) {
                return;
              }

              if (additionalTechniquesArtSettings) {
                await dispatch(
                  editAdditionalTechniquesArtSettings({
                    ID: selectedProduct?.Product?.ID,
                    additionalTechniquesArtSettings,
                  })
                );
              }

              setProduct(undefined);
            }}
            updateProductInformation={async (payload) => {
              const request = await dispatch(
                editProducts({
                  productData: {
                    ...payload,
                    Description: draftToHtml(
                      convertToRaw(payload?.Description.getCurrentContent())
                    ),
                  },
                })
              );
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            updateProductSizes={async (payload) => {
              const request = await dispatch(editProductSize(payload));
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            editColorsInformation={async (payload) => {
              const request = await dispatch(editColorsInformation(payload));
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            editDesignerSettings={async (payload) => {
              const request = await dispatch(editDesignerSettings(payload));
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            editAdditionalTechniquesArtSettings={async (payload) => {
              const request = await dispatch(
                editAdditionalTechniquesArtSettings(payload)
              );
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            editProductArtworkSettings={async (payload) => {
              const request = await dispatch(editArtworkSettings(payload));
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            updateProductPrintProviders={async (payload) => {
              const request = await dispatch(
                editPrintProvidersInformation(payload)
              );
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            editDTGPlusInformation={async (payload) => {
              const request = await dispatch(editDTGPlusInformation(payload));
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
            addAdditionalImages={async (payload, clearImages) => {
              if (clearImages) {
                await dispatch(clearAdditionalImages(clearImages));
              }

              const request = await dispatch(
                editProductAdditionalImages(payload)
              );
              if (request.error) {
                return;
              }
            }}
            deleteProduct={async () => {
              const request = await dispatch(
                deleteProduct({
                  ID: selectedProduct?.Product?.ID,
                })
              );
              if (request.error) {
                return;
              }
              setProduct(undefined);
            }}
          />
        )}
      </Container>
      {shirtlySyncProducts && shirtlySyncProducts?.length && (
        <ShirtlyProductSync
          open={!!shirtlySyncProducts}
          setModal={() => setShirtlySyncProducts(undefined)}
          products={shirtlySyncProducts}
          saveToBot={async (products) => {
            const response = await dispatch(saveProductsInBot(products));

            if (response?.error) {
              return;
            }
            alert("Products added successfully to BOT");
            setShirtlySyncProducts(undefined);
          }}
        />
      )}
    </div>
  );
};

export default EditProducts;
